import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, Paper, Stack, TextField, Typography } from '@mui/material';
import api from '../../../services/api/Proyecto';

const date = value => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString('es-GT');
};
const errorText = error => error.response?.data?.message || 'No se pudo consultar la bandeja. Intenta nuevamente.';

export default function BandejaDialog({ email, webmailUrl, onClose }) {
  const base = `/api/cuentas-correo/${encodeURIComponent(email)}/inbox`;
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [linkRequired, setLinkRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState(null);
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null); setSelected(null);
    api.get(base, { params: { page }, signal: controller.signal }).then(response => {
      if (!controller.signal.aborted) { setData(response.data); setLinkRequired(false); }
    }).catch(e => {
      if (controller.signal.aborted) return;
      setError(errorText(e));
      if (['MAILBOX_NOT_LINKED', 'MAILBOX_AUTH_FAILED'].includes(e.response?.data?.code)) setLinkRequired(true);
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [base, page, revision]);

  useEffect(() => {
    setMessage(null); setMessageError('');
    if (!selected) { setMessageLoading(false); return undefined; }
    const controller = new AbortController();
    setMessageLoading(true);
    api.get(`${base}/messages/${selected.uid}`, { params: { uidValidity: selected.validity }, signal: controller.signal }).then(response => {
      if (!controller.signal.aborted) setMessage(response.data);
    }).catch(e => {
      if (controller.signal.aborted) return;
      setMessageError(errorText(e));
      if (e.response?.data?.code === 'MAILBOX_AUTH_FAILED') setLinkRequired(true);
    }).finally(() => { if (!controller.signal.aborted) setMessageLoading(false); });
    return () => controller.abort();
  }, [base, selected]);

  const refresh = () => { setPage(0); setRevision(n => n + 1); };
  const link = async event => {
    event.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true); setError('');
    try {
      await api.post(`${base}/link`, { password });
      setPassword(''); setLinkRequired(false); refresh();
    } catch (e) { setPassword(''); setError(errorText(e)); }
    finally { savingRef.current = false; setSaving(false); }
  };
  const unlink = async () => {
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true); setError('');
    try {
      await api.delete(`${base}/link`);
      setData(null); setSelected(null); setPassword(''); setLinkRequired(true); setConfirmUnlink(false);
    } catch (e) { setError(errorText(e)); }
    finally { savingRef.current = false; setSaving(false); }
  };
  return <Dialog open onClose={() => { if (!savingRef.current) onClose(); }} fullWidth maxWidth="lg" aria-labelledby="bandeja-title">
    <DialogTitle id="bandeja-title" sx={{ overflowWrap: 'anywhere' }}>Bandeja de entrada · {email}</DialogTitle>
    <DialogContent dividers>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress aria-label="Cargando bandeja" /></Box> : linkRequired ?
        <Box component="form" onSubmit={link} sx={{ maxWidth: 480, mx: 'auto', py: 3 }}>
          <Typography variant="h6" gutterBottom>Vincular buzón</Typography>
          <Typography color="text.secondary" mb={2}>Ingresa una vez la contraseña de este correo. Se guardará cifrada en el servidor para abrir su bandeja desde el sistema.</Typography>
          <TextField fullWidth autoFocus label="Contraseña del correo" type="password" autoComplete="off" value={password} disabled={saving}
            inputProps={{ maxLength: 1024 }} required onChange={e => setPassword(e.target.value)} />
          <Button type="submit" variant="contained" disabled={saving || !password} sx={{ mt: 2 }}>{saving ? 'Verificando…' : 'Vincular y abrir bandeja'}</Button>
        </Box> : data && <>
          <Stack direction="row" gap={1} useFlexGap flexWrap="wrap" alignItems="center" mb={2}>
            <Typography sx={{ flex: 1 }}>{data.total} mensaje(s) · Vista de solo lectura</Typography>
            <Button disabled={saving} onClick={refresh}>Actualizar mensajes</Button>
            <Button disabled={saving} onClick={() => { setLinkRequired(true); setPassword(''); }}>Actualizar acceso</Button>
            <Button disabled={saving} color="warning" onClick={() => setConfirmUnlink(true)}>Desvincular</Button>
          </Stack>
          {confirmUnlink && <Alert severity="warning" sx={{ mb: 2 }} action={<Stack direction="row"><Button disabled={saving} onClick={() => setConfirmUnlink(false)}>Cancelar</Button><Button disabled={saving} color="warning" onClick={unlink}>Confirmar</Button></Stack>}>Se quitará el acceso guardado. La cuenta y sus correos se conservan.</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 2fr) minmax(0, 3fr)' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ maxHeight: { xs: 300, md: 560 }, overflowY: 'auto' }}>
              <List disablePadding aria-label="Mensajes de la bandeja">
                {data.messages.map(item => <ListItemButton key={item.uid} selected={selected?.uid === item.uid} alignItems="flex-start"
                  onClick={() => setSelected({ uid: item.uid, validity: data.uidValidity })} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Typography fontWeight={item.unseen ? 800 : 500} sx={{ overflowWrap: 'anywhere' }}>{item.subject}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{item.from}</Typography>
                    <Typography variant="caption" color="text.secondary">{date(item.date)}{item.unseen ? ' · No leído' : ''}</Typography>
                  </Box>
                </ListItemButton>)}
              </List>
              {!data.messages.length && <Typography sx={{ p: 3 }}>{data.total ? 'No hay mensajes en esta página. Actualiza la bandeja.' : 'La bandeja está vacía.'}</Typography>}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 250, maxHeight: 560, overflowY: 'auto', overflowWrap: 'anywhere' }}>
              {messageLoading ? <CircularProgress aria-label="Cargando mensaje" /> : messageError ? <Alert severity="error">{messageError}</Alert> : message ? <>
                <Typography variant="h6" gutterBottom>{message.subject}</Typography>
                <Typography variant="body2">De: {message.from}</Typography>
                <Typography variant="body2">Para: {message.to}</Typography>
                <Typography variant="caption" color="text.secondary">{date(message.date)}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.text || '(Mensaje sin contenido de texto)'}</Typography>
                {message.truncated && <Alert severity="info" sx={{ mt: 2 }}>El texto es muy largo. Abre Webmail externo para ver el mensaje completo.</Alert>}
                {message.attachments?.length > 0 && <Box sx={{ mt: 3 }}><Divider sx={{ mb: 2 }} /><Typography fontWeight={700}>Archivos adjuntos</Typography>
                  {message.attachments.map((a, i) => <Typography key={i} variant="body2">{a.filename} · {Math.ceil((a.size || 0) / 1024)} KB</Typography>)}
                  <Typography variant="caption" color="text.secondary">Para descargarlos utiliza Webmail externo.</Typography>
                </Box>}
              </> : <Typography color="text.secondary">Selecciona un mensaje para leerlo.</Typography>}
            </Paper>
          </Box>
          <Stack direction="row" gap={2} justifyContent="center" alignItems="center" mt={2}>
            <Button disabled={page === 0} onClick={() => setPage(n => n - 1)}>Más recientes</Button>
            <Typography variant="body2">Página {page + 1}</Typography>
            <Button disabled={!data.hasMore} onClick={() => setPage(n => n + 1)}>Más antiguos</Button>
          </Stack>
        </>}
      {!loading && !linkRequired && !data && <Button onClick={refresh}>Reintentar</Button>}
    </DialogContent>
    <DialogActions>
      <Button component="a" href={webmailUrl} target="_blank" rel="noopener noreferrer">Webmail externo</Button>
      <Button disabled={saving} onClick={onClose}>Cerrar</Button>
    </DialogActions>
  </Dialog>;
}
